// Course content — verbatim from user input.
// Editing this file is the source of truth for lesson/module copy.

export const COURSE = {
  title: "The Hormone-Gut Axis",
  subtitle: "How Your Microbiome Controls Estrogen",
  modules: [
    {
      id: 1,
      title: "What Is Estrogen?",
      icon: "🌸",
      colorVar: "--mod-1",
      lessons: [
        {
          id: "1a",
          title: "The Three Estrogens",
          duration: "5 min",
          content: {
            intro:
              "Estrogen isn't one hormone — it's a family of three. Understanding each one is the foundation of everything that follows.",
            sections: [
              {
                heading: "The Estrogen Family",
                body: "All three estrogens are derived from cholesterol and produced primarily in the ovaries, but also in the adrenal glands and fat (adipose) tissue.",
                diagram: {
                  type: "cards",
                  items: [
                    {
                      label: "Estradiol (E2)",
                      sub: "Reproductive years",
                      desc: "The most potent estrogen. Governs the menstrual cycle, bone density, mood, cardiovascular health, and cognition.",
                      colorVar: "--mod-1",
                    },
                    {
                      label: "Estriol (E3)",
                      sub: "Pregnancy",
                      desc: "Surges during pregnancy. Lowest potency. Thought to be protective against some cancers.",
                      colorVar: "--info",
                    },
                    {
                      label: "Estrone (E1)",
                      sub: "Post-menopause",
                      desc: "Produced mainly in fat cells. Becomes the dominant estrogen after menopause. Linked to breast cancer risk at high levels.",
                      colorVar: "--danger",
                    },
                  ],
                },
              },
              {
                heading: "Where Estrogen Is Made",
                body: "Most people think estrogen is only an ovarian hormone. In reality, it's produced in multiple sites — which is why it persists even after menopause.",
                diagram: {
                  type: "flow",
                  items: [
                    { icon: "🫀", label: "Ovaries", note: "Primary source in reproductive years" },
                    { icon: "🧠", label: "Adrenal Glands", note: "Small but consistent production" },
                    { icon: "🫙", label: "Fat Cells (Adipose)", note: "Converts androgens → estrogen via aromatase enzyme" },
                    { icon: "🦠", label: "Gut Microbiome", note: "Regulates circulating estrogen levels through the estrobolome" },
                  ],
                },
              },
              {
                heading: "Why Estrogen Is a 'Lovely' Hormone in Balance",
                body: "When estrogen is balanced with progesterone, it's profoundly beneficial: it protects bone density, supports memory and cognition, reduces cardiovascular risk, supports mood and libido, and regulates the menstrual cycle. The problem arises when it goes unchecked.",
              },
            ],
            flashcards: [
              { q: "Which estrogen dominates during reproductive years?", a: "Estradiol (E2) — the most potent form, produced primarily by the ovaries." },
              { q: "Which cells convert androgens into estrogen?", a: "Fat cells (adipose tissue), via an enzyme called aromatase." },
              { q: "Why can estrogen dominance occur even after menopause?", a: "Because fat cells continue producing estrone (E1) via aromatase, even without ovarian function." },
            ],
          },
        },
        {
          id: "1b",
          title: "Estrogen Dominance Defined",
          duration: "6 min",
          content: {
            intro:
              "Estrogen dominance doesn't necessarily mean you have too much estrogen. It means estrogen is high relative to progesterone — the hormone that should balance it.",
            sections: [
              {
                heading: "The Progesterone–Estrogen Balance",
                body: "Harvard physician Dr. John R. Lee coined 'estrogen dominance' to describe a state where estrogen is elevated in relation to progesterone — even if estrogen itself is in normal range. Without progesterone's balancing influence, estrogen can drive cell proliferation, inflammation, and a cascade of symptoms.",
                diagram: {
                  type: "balance",
                  left: { label: "Estrogen", icon: "🔥", desc: "Growth, proliferation, inflammation when excess" },
                  right: { label: "Progesterone", icon: "❄️", desc: "Calming, anti-inflammatory, balances estrogen" },
                },
              },
              {
                heading: "Symptoms of Estrogen Dominance",
                body: "Estrogen dominance affects virtually every system in the body:",
                diagram: {
                  type: "symptom-grid",
                  items: [
                    { system: "Reproductive", symptoms: ["Heavy, painful periods", "Uterine fibroids", "Endometriosis", "PCOS", "Breast tenderness"] },
                    { system: "Metabolic", symptoms: ["Weight gain (especially hips/thighs)", "Blood sugar dysregulation", "Thyroid dysfunction"] },
                    { system: "Neurological", symptoms: ["Mood swings", "Anxiety & depression", "Brain fog", "Insomnia"] },
                    { system: "Immune", symptoms: ["Autoimmune conditions", "Chronic inflammation", "Increased cancer risk"] },
                  ],
                },
              },
            ],
            flashcards: [
              { q: "Who coined the term 'estrogen dominance' and what does it mean?", a: "Dr. John R. Lee. It means estrogen is high relative to progesterone — not necessarily that absolute estrogen is high." },
              { q: "Name 3 reproductive conditions associated with estrogen dominance.", a: "Endometriosis, uterine fibroids, and PCOS." },
              { q: "What happens to progesterone under chronic stress?", a: "The body 'steals' progesterone to make cortisol, leaving insufficient progesterone to balance estrogen." },
            ],
          },
        },
      ],
    },
    {
      id: 2,
      title: "The Estrobolome",
      icon: "🦠",
      colorVar: "--mod-2",
      lessons: [
        {
          id: "2a",
          title: "What Is the Estrobolome?",
          duration: "7 min",
          content: {
            intro:
              "The estrobolome is one of the most important concepts in women's health that most people have never heard of. Defined in 2011, it refers to the collection of gut bacteria — and their genes — capable of metabolizing estrogen.",
            sections: [
              {
                heading: "Defining the Estrobolome",
                body: "The estrobolome is a subset of your gut microbiome specifically responsible for estrogen metabolism. These bacteria produce an enzyme called β-glucuronidase (beta-glucuronidase), which is the key player in whether estrogen gets recycled back into your bloodstream or eliminated through your stool.",
                diagram: {
                  type: "definition-box",
                  term: "Estrobolome",
                  definition: "The aggregate of enteric bacterial genes capable of metabolizing estrogens. First defined by Plottel & Blaser in 2011.",
                  key_enzyme: "β-glucuronidase (GUS gene)",
                  key_bacteria: ["Bacteroidetes", "Firmicutes", "Lactobacillus", "Bifidobacterium", "E. coli", "Ruminococcaceae"],
                },
              },
              {
                heading: "The Estrogen Journey: Liver → Gut → Blood",
                body: "To understand the estrobolome, you need to understand how estrogen normally travels through your body.",
                diagram: {
                  type: "pathway",
                  steps: [
                    { num: "1", label: "Production", body: "Estrogen produced in ovaries, adrenals, fat cells", colorVar: "--mod-1" },
                    { num: "2", label: "Liver Phase 1 & 2", body: "Liver conjugates (deactivates) estrogen, tagging it for elimination", colorVar: "--warning" },
                    { num: "3", label: "Bile → Small Intestine", body: "Conjugated (inactive) estrogen excreted into bile, flows into gut", colorVar: "--info" },
                    { num: "4", label: "The Fork: Excrete or Recirculate?", body: "β-glucuronidase bacteria DECONJUGATE estrogen → reactivates it → reabsorbed into bloodstream (enterohepatic circulation)", colorVar: "--mod-2" },
                    { num: "5", label: "OR: Eliminated", body: "If β-glucuronidase activity is balanced, estrogen exits in stool", colorVar: "--muted" },
                  ],
                },
              },
            ],
            flashcards: [
              { q: "What is the estrobolome?", a: "The collection of gut bacteria and their genes capable of metabolizing estrogen, first defined in 2011." },
              { q: "What enzyme do estrobolome bacteria produce that reactivates estrogen?", a: "β-glucuronidase (encoded by the GUS gene)." },
              { q: "What is enterohepatic circulation of estrogen?", a: "The recycling loop where reactivated estrogen is reabsorbed from the gut back into the bloodstream, increasing circulating estrogen levels." },
            ],
          },
        },
        {
          id: "2b",
          title: "β-Glucuronidase: The Key Enzyme",
          duration: "8 min",
          content: {
            intro:
              "β-glucuronidase is the molecular switch between estrogen balance and estrogen dominance. Too much of it = too much circulating estrogen. Too little = hormonal deficiency.",
            sections: [
              {
                heading: "How β-Glucuronidase Works",
                body: "The liver conjugates estrogen — essentially adding a glucuronic acid tag that deactivates it and marks it for excretion. β-glucuronidase enzymes produced by gut bacteria cleave this tag, reactivating the estrogen and allowing it to be reabsorbed through the gut wall back into circulation.",
                diagram: {
                  type: "enzyme-diagram",
                  steps: [
                    { icon: "💊", label: "Conjugated Estrogen", note: "Inactive — tagged by liver for elimination" },
                    { icon: "🦠", label: "β-Glucuronidase", note: "Gut bacteria enzyme cleaves the glucuronic acid tag" },
                    { icon: "⚡", label: "Free (Active) Estrogen", note: "Reactivated — now binds to estrogen receptors ERα and ERβ" },
                    { icon: "🔄", label: "Reabsorbed into Blood", note: "Enters enterohepatic circulation, raising systemic estrogen" },
                  ],
                },
              },
              {
                heading: "The Two-Sided Problem",
                body: "β-glucuronidase activity can go wrong in both directions:",
                diagram: {
                  type: "two-column",
                  left: {
                    title: "TOO MUCH β-glucuronidase",
                    colorVar: "--danger",
                    points: [
                      "Dysbiosis (high Firmicutes:Bacteroidetes ratio)",
                      "High-fat diet, low fiber",
                      "Excessive estrogen reabsorption",
                      "→ Estrogen dominance",
                      "→ Endometriosis, fibroids, breast cancer risk",
                    ],
                  },
                  right: {
                    title: "TOO LITTLE β-glucuronidase",
                    colorVar: "--info",
                    points: [
                      "Dysbiosis (low microbial diversity)",
                      "Antibiotics, poor diet",
                      "Insufficient estrogen recycling",
                      "→ Low circulating estrogen",
                      "→ Metabolic syndrome, cognitive decline, osteoporosis",
                    ],
                  },
                },
              },
              {
                heading: "Calcium D-Glucarate: The Blocker",
                body: "Calcium D-glucarate is a supplement that blocks β-glucuronidase activity — not permanently removing it, but reducing excessive reabsorption. Used short-term (3-6 months) while gut health is being addressed through diet and probiotics.",
              },
            ],
            flashcards: [
              { q: "What does β-glucuronidase do to conjugated estrogen?", a: "It removes the glucuronic acid tag (deconjugates), reactivating the estrogen so it can be reabsorbed into the bloodstream." },
              { q: "Which bacterial phyla are the primary sources of β-glucuronidase?", a: "Bacteroidetes and Firmicutes. A high Firmicutes:Bacteroidetes ratio increases β-glucuronidase activity." },
              { q: "What is calcium D-glucarate and when is it used?", a: "A supplement that blocks β-glucuronidase activity. Used short-term to reduce estrogen reabsorption while the gut microbiome is being healed." },
            ],
          },
        },
      ],
    },
    {
      id: 3,
      title: "Causes of Dysbiosis",
      icon: "⚠️",
      colorVar: "--mod-3",
      lessons: [
        {
          id: "3a",
          title: "What Disrupts the Estrobolome?",
          duration: "7 min",
          content: {
            intro:
              "Dysbiosis — an imbalance in gut microbiota — is the upstream trigger for estrobolome disruption. Modern life is remarkably good at causing it.",
            sections: [
              {
                heading: "The Root Causes",
                body: "The estrobolome is disrupted by the same things that damage the overall gut microbiome:",
                diagram: {
                  type: "cause-web",
                  items: [
                    { icon: "🥩", label: "High-fat, low-fiber diet", note: "Promotes Firmicutes overgrowth → more β-glucuronidase" },
                    { icon: "💊", label: "Antibiotics", note: "Destroy beneficial bacteria, reduce microbial diversity" },
                    { icon: "😰", label: "Chronic stress", note: "Cortisol disrupts gut barrier integrity; steals progesterone" },
                    { icon: "🧴", label: "Xenoestrogens", note: "BPA, phthalates, pesticides mimic estrogen, disrupt microbiome" },
                    { icon: "🍷", label: "Alcohol", note: "2+ drinks/day impairs liver detox and microbiome balance" },
                    { icon: "💉", label: "Oral contraceptives", note: "Alter gut flora and impair estrogen detox pathways" },
                    { icon: "🏙️", label: "Environmental toxins", note: "DDT, chlorine, plastics — all estrogenic or microbiome-disrupting" },
                    { icon: "🛋️", label: "Sedentary lifestyle", note: "Reduces microbial diversity and increases adipose aromatase activity" },
                  ],
                },
              },
              {
                heading: "Xenoestrogens: The Hidden Amplifier",
                body: "Xenoestrogens are synthetic chemicals that mimic estrogen. They enter through food, plastics, cosmetics, and cleaning products. The WHO links them to altered reproductive function, breast cancer, neurodevelopmental delays, and immune dysfunction. BPA, phthalates, DDT residues, and pesticides are among the most common.",
              },
              {
                heading: "The Liver Connection",
                body: "The liver is responsible for Phase 1 and Phase 2 estrogen detoxification. Poor liver function — from alcohol, medications, nutrient deficiencies (especially magnesium and B vitamins), or excess toxin load — means estrogen isn't properly conjugated before it reaches the gut. This overwhelms the estrobolome.",
              },
            ],
            flashcards: [
              { q: "What dietary factor most promotes β-glucuronidase overactivity?", a: "A high-fat, low-fiber diet — particularly saturated fats from meat, which promote Firmicutes bacteria." },
              { q: "What are xenoestrogens?", a: "Synthetic chemicals that mimic estrogen. Found in plastics (BPA, phthalates), pesticides, cleaning products, and personal care items." },
              { q: "How does chronic stress contribute to estrogen dominance?", a: "Stress hormone cortisol production 'steals' progesterone as a precursor, reducing the progesterone available to balance estrogen." },
            ],
          },
        },
      ],
    },
    {
      id: 4,
      title: "Healing the Estrobolome",
      icon: "🌿",
      colorVar: "--mod-4",
      lessons: [
        {
          id: "4a",
          title: "Diet & Fiber",
          duration: "6 min",
          content: {
            intro:
              "Fiber is the single most powerful dietary tool for estrogen balance. It feeds beneficial bacteria, binds excess estrogen in the gut, and promotes elimination.",
            sections: [
              {
                heading: "The Anti-Estrogen Dominance Diet",
                body: "Women eating a plant-based, high-fiber diet have significantly lower blood estrogen and higher fecal estrogen excretion. The mechanism is simple: fiber binds to estrogen in the gut, preventing reabsorption.",
                diagram: {
                  type: "food-grid",
                  categories: [
                    { title: "Cruciferous Vegetables ⭐", colorVar: "--mod-2", foods: ["Broccoli", "Cauliflower", "Kale", "Brussels sprouts", "Arugula", "Collard greens"], note: "Contain DIM (diindolylmethane) — supports Phase 1 liver detox of estrogen" },
                    { title: "High-Fiber Foods", colorVar: "--info", foods: ["Flaxseed", "Chia seeds", "Oats", "Legumes", "Psyllium husk", "Berries"], note: "Binds estrogen in the gut; feeds Lactobacillus and Bifidobacterium" },
                    { title: "Liver Support", colorVar: "--warning", foods: ["Beets", "Dandelion", "Milk thistle", "Artichoke", "Turmeric", "Garlic"], note: "Supports Phase 1 & 2 liver detox pathways" },
                    { title: "Reduce / Avoid", colorVar: "--danger", foods: ["Conventional meat & dairy", "Processed foods", "Alcohol", "Refined sugar", "Plastics (BPA)"], note: "Promote dysbiosis, xenoestrogen exposure, and liver burden" },
                  ],
                },
              },
              {
                heading: "Phytoestrogens: Friend or Foe?",
                body: "Phytoestrogens (found in soy, flaxseed, and legumes) are plant-based compounds that weakly bind to estrogen receptors. They can actually have a protective effect — competing with stronger estrogens for receptor binding and reducing overall estrogenic activity. The gut microbiome converts them into their active forms.",
              },
            ],
            flashcards: [
              { q: "What compound in cruciferous vegetables supports estrogen detox?", a: "DIM (diindolylmethane), which supports Phase 1 liver detoxification of estrogen." },
              { q: "How does fiber reduce circulating estrogen?", a: "It binds to estrogen in the gut, preventing reabsorption and promoting elimination through stool." },
              { q: "Are phytoestrogens harmful?", a: "Generally not — they weakly bind estrogen receptors and can actually reduce overall estrogenic activity by competing with stronger estrogens." },
            ],
          },
        },
        {
          id: "4b",
          title: "Probiotics, Supplements & Testing",
          duration: "7 min",
          content: {
            intro:
              "Beyond diet, targeted interventions can restore the estrobolome. But testing first is essential — you need to know what you're working with.",
            sections: [
              {
                heading: "Probiotics for Estrogen Balance",
                body: "Specific bacterial strains are associated with healthy estrogen metabolism:",
                diagram: {
                  type: "probiotic-table",
                  items: [
                    { bacteria: "Lactobacillus spp.", role: "Produces β-glucuronidase in balanced amounts; produces lactic acid protecting gut lining", source: "Yogurt, kefir, kimchi, lactobacillus supplements" },
                    { bacteria: "Bifidobacterium spp.", role: "Supports bile acid metabolism; reduces inflammatory dysbiosis", source: "Aged cheese, kefir, bifido probiotic supplements" },
                    { bacteria: "Ruminococcaceae", role: "Estrogen-metabolizing activity; reduced in dysbiosis", source: "High-fiber diet supports its growth" },
                  ],
                },
              },
              {
                heading: "Key Supplements",
                diagram: {
                  type: "supplement-list",
                  items: [
                    { name: "Calcium D-Glucarate", action: "Blocks β-glucuronidase activity; reduces estrogen reabsorption", timing: "Short-term (3-6 months) while healing gut" },
                    { name: "DIM (Diindolylmethane)", action: "Supports liver Phase 1 estrogen detox; shifts metabolism toward protective estrogen metabolites", timing: "Medium-term; monitor with testing" },
                    { name: "Magnesium", action: "Cofactor for liver estrogen detox enzymes (Phase 2)", timing: "Long-term maintenance" },
                    { name: "B6, B12, Folate", action: "Methylation support — Phase 2 liver detox", timing: "Long-term maintenance" },
                    { name: "Probiotics (Lactobacillus + Bifido)", action: "Restore healthy estrobolome function", timing: "Ongoing" },
                  ],
                },
              },
              {
                heading: "Testing: Know Before You Treat",
                body: "Never self-treat hormonal imbalances without testing. Symptoms of estrogen dominance can also occur with low estrogen. Key tests include the DUTCH Complete (urine test — most comprehensive for estrogen metabolites), saliva cortisol, comprehensive stool test (looks at β-glucuronidase activity directly), and standard blood hormone panels (estradiol, progesterone, testosterone, FSH, LH).",
              },
            ],
            flashcards: [
              { q: "What test best assesses estrogen metabolite patterns?", a: "The DUTCH Complete test (dried urine) — shows estrogen metabolites from Phase 1 and Phase 2 liver detox pathways." },
              { q: "Name two supplements used to support estrogen detoxification.", a: "DIM (supports liver Phase 1) and Calcium D-Glucarate (blocks β-glucuronidase activity in the gut)." },
              { q: "Why is magnesium important for estrogen metabolism?", a: "It's a cofactor for Phase 2 liver detox enzymes that conjugate and eliminate estrogen." },
            ],
          },
        },
      ],
    },
  ],
};
